// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="ColorPicker" set="mytaible-shadcn-studio" verified=tbd
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { Input } from './input';

// ─── Presets ──────────────────────────────────────────────────────────────────

export const COLOR_PRESETS = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#10b981',
  '#06b6d4',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#6b7280',
  '#1f2937',
];

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ColorPickerProps {
  /** Controlled hex value, e.g. `'#3b82f6'`. */
  value?: string;
  /** Called with the new hex string whenever the selection changes. */
  onChange?: (color: string) => void;
  /** Override the preset swatch grid. Defaults to `COLOR_PRESETS`. */
  presets?: string[];
  /** Disables all interaction. */
  disabled?: boolean;
  /** Extra CSS classes merged onto the root `<div>`. */
  className?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isValidHex = (v: string) => /^#[0-9a-fA-F]{6}$/.test(v);

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Color picker with preset swatches and a hex code text input.
 *
 * - **Swatches** — click any swatch to select that colour immediately.
 * - **Hex input** — type or paste a 6-digit hex code; the preview square
 *   updates on every valid keystroke and on blur.
 * - Fully keyboard accessible: swatches receive `aria-pressed` and support
 *   focus-visible ring; the hex input receives `aria-label`.
 *
 * @example
 * const [color, setColor] = React.useState('#3b82f6');
 * <ColorPicker value={color} onChange={setColor} />
 */
const ColorPicker = React.forwardRef<HTMLInputElement, ColorPickerProps>(
  ({ value = '', onChange, presets = COLOR_PRESETS, disabled, className }, ref) => {
    const [hexInput, setHexInput] = React.useState(value);

    // Keep local input in sync when controlled value changes externally.
    React.useEffect(() => {
      setHexInput(value);
    }, [value]);

    const commitHex = (raw: string) => {
      const hex = raw.startsWith('#') ? raw : `#${raw}`;
      if (isValidHex(hex)) onChange?.(hex.toLowerCase());
    };

    const pickSwatch = (color: string) => {
      setHexInput(color);
      onChange?.(color);
    };

    return (
      <div className={cn('space-y-3 w-full', className)}>
        {/* ── Swatches ──────────────────────────────────────────────────────── */}
        {/* biome-ignore lint/a11y/useSemanticElements: role="group" is correct for grouped toggle buttons */}
        <div className="flex flex-wrap gap-2" role="group" aria-label="Color presets">
          {presets.map((color) => (
            <button
              key={color}
              type="button"
              disabled={disabled}
              aria-label={`Select color ${color}`}
              aria-pressed={value === color}
              onClick={() => pickSwatch(color)}
              className={cn(
                'size-8 rounded-md border-2 transition-all',
                'hover:scale-110 hover:shadow-md',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                'disabled:cursor-not-allowed disabled:opacity-50',
                value === color ? 'border-foreground shadow-sm' : 'border-transparent',
              )}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>

        {/* ── Hex input row ─────────────────────────────────────────────────── */}
        <div className="flex items-center gap-2">
          {/* Colour preview square */}
          <div
            aria-hidden="true"
            className={cn('size-9 shrink-0 rounded-md border border-border', !value && 'bg-muted')}
            style={value ? { backgroundColor: value } : undefined}
          />
          <Input
            ref={ref}
            value={hexInput}
            onChange={(e) => {
              setHexInput(e.target.value);
              commitHex(e.target.value);
            }}
            onBlur={() => commitHex(hexInput)}
            placeholder="#000000"
            disabled={disabled}
            className="font-mono uppercase"
            maxLength={7}
            aria-label="Hex color code"
          />
        </div>
      </div>
    );
  },
);
ColorPicker.displayName = 'ColorPicker';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { ColorPicker };
